import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ArrowLeft, Package } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'failed'
  const [orderDetails, setOrderDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setOrderDetails(data);
          clearCart(); // Clear cart after successful payment
          return true;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return true;
        }

        return false;
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts >= 4) {
          setStatus('failed');
          return true;
        }
        return false;
      }
    };

    // Poll for payment status
    const pollStatus = async () => {
      const isComplete = await checkPaymentStatus();
      if (!isComplete && attempts < 5) {
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2000);
      }
    };

    pollStatus();
  }, [sessionId, attempts, clearCart]);

  return (
    <div className="min-h-screen bg-gray-100 pt-24 px-4">
      <div className="max-w-lg mx-auto">
        {status === 'checking' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <Loader2 className="mx-auto animate-spin text-emerald-600 mb-4" size={64} />
            <h1 
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Processing Payment...
            </h1>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-600" size={48} />
            </div>
            <h1 
              className="text-3xl font-bold text-green-700 mb-2"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Order Confirmed!
            </h1>
            <p className="text-gray-600 mb-6">
              Thank you for your order. We'll contact you soon to arrange {orderDetails?.fulfillment_type === 'delivery' ? 'delivery' : 'pickup'}.
            </p>
            
            {orderDetails?.order_id && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500">Order ID</p>
                <p className="font-mono text-sm">{orderDetails.order_id}</p>
              </div>
            )}

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-emerald-700">
                <Package size={20} />
                <span className="font-medium">What's Next?</span>
              </div>
              <p className="text-sm text-emerald-600 mt-2">
                You'll receive a confirmation email shortly. We'll contact you to coordinate {orderDetails?.fulfillment_type === 'delivery' ? 'delivery details' : 'your pickup time'}.
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/nursery-shop')}
                className="w-full bg-black hover:bg-gray-800"
              >
                Continue Shopping
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back to Home
              </Button>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-red-600" size={48} />
            </div>
            <h1 
              className="text-3xl font-bold text-red-700 mb-2"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Payment Issue
            </h1>
            <p className="text-gray-600 mb-6">
              We couldn't confirm your payment. If you believe this is an error, please contact us.
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/nursery-shop')}
                className="w-full bg-black hover:bg-gray-800"
              >
                Return to Shop
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back to Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderSuccess;
